import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, TrendingUp, TrendingDown, Minus, RefreshCw, ArrowRight } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import PrintPageButton from "@/components/PrintPageButton";

const bandColor = (band) => {
    switch (band) {
        case "Excellent": return { fg: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", ring: "ring-emerald-200" };
        case "Strong": return { fg: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200", ring: "ring-teal-200" };
        case "Fair": return { fg: "text-amber-800", bg: "bg-amber-50", border: "border-amber-200", ring: "ring-amber-200" };
        case "Needs Work": return { fg: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200", ring: "ring-orange-200" };
        default: return { fg: "text-red-700", bg: "bg-red-50", border: "border-red-200", ring: "ring-red-200" };
    }
};

const TrendIcon = ({ trend }) => {
    if (trend === "up") return <TrendingUp size={12} className="text-emerald-600" />;
    if (trend === "down") return <TrendingDown size={12} className="text-red-600" />;
    if (trend === "flat") return <Minus size={12} className="text-stone-500" />;
    return null;
};

export default function HealthScore() {
    const [data, setData] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/health-score");
            setData(d);
            const { data: h } = await api.get("/health-score/history?days=60");
            setHistory((h.series || []).map((s) => ({ date: s.date, score: s.score })));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="text-stone-500 text-sm" data-testid="hs-loading">Computing your score…</div>;
    const c = bandColor(data.band);
    const nudges = (data.factors || []).filter((f) => f.recommendation).slice(0, 3);

    return (
        <div data-testid="health-score-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Activity size={12} /> Financial Health</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        How healthy is your money?
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        A composite score across utilization, on-time payments, diversification, insurance,
                        subscription health, upcoming liabilities and net-worth trend. Recomputed live from
                        your data.
                    </p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} data-testid="hs-refresh">
                    <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Recompute
                </Button>
                <PrintPageButton />
            </div>

            {/* Big score card */}
            <div className={`rounded-xl border ${c.border} ${c.bg} p-8 mb-6`} data-testid="hs-score-card">
                <div className="flex items-center justify-between gap-6 flex-wrap">
                    <div>
                        <div className="eyebrow">Your score</div>
                        <div className="flex items-baseline gap-3 mt-2">
                            <div className={`font-display text-7xl leading-none ${c.fg}`} data-testid="hs-score">{data.score}</div>
                            <div className="text-stone-500 font-mono-figure text-lg">/ {data.max}</div>
                        </div>
                        <div className={`inline-flex items-center gap-1.5 mt-3 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${c.border} bg-white`}>
                            <span className={c.fg}>{data.band}</span>
                            <span className="text-stone-500">· {data.percent}%</span>
                        </div>
                    </div>
                    {history.length > 1 && (
                        <div className="flex-1 min-w-[260px] max-w-md" data-testid="hs-sparkline">
                            <div className="eyebrow mb-2">Last {history.length} day{history.length === 1 ? "" : "s"}</div>
                            <div style={{ width: "100%", height: 68 }}>
                                <ResponsiveContainer>
                                    <LineChart data={history} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                                        <YAxis domain={[0, 100]} hide />
                                        <Tooltip
                                            contentStyle={{ fontSize: 11, padding: 6 }}
                                            formatter={(v) => [`${v} pts`, "Score"]}
                                            labelFormatter={(l) => l}
                                        />
                                        <Line type="monotone" dataKey="score" stroke="#0f766e" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                    {nudges.length > 0 && (
                        <div className="flex-1 min-w-[280px] max-w-xl">
                            <div className="eyebrow mb-2">Top actions</div>
                            <ul className="space-y-1.5">
                                {nudges.map((n) => (
                                    <li key={n.id} className="text-sm text-stone-800" data-testid={`hs-nudge-${n.id}`}>
                                        <span className="text-stone-500">{n.label}:</span> {n.recommendation}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Factor breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(data.factors || []).map((fac) => {
                    const pct = fac.max ? (fac.points / fac.max) * 100 : 0;
                    const barColor = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
                    return (
                        <div key={fac.id} className="rounded-lg border border-stone-200 bg-white p-5" data-testid={`hs-factor-${fac.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="text-sm font-medium text-stone-900">{fac.label}</div>
                                <TrendIcon trend={fac.trend} />
                            </div>
                            <div className="flex items-baseline gap-1 mt-2">
                                <div className="font-mono-figure text-2xl text-stone-900">{fac.points}</div>
                                <div className="text-stone-500 text-[12px]">/ {fac.max}</div>
                            </div>
                            <div className="mt-3 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                                <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                            <div className="text-[12px] text-stone-500 mt-3 leading-relaxed">{fac.detail}</div>
                            {fac.recommendation && (
                                <div className="text-[12px] text-stone-800 mt-2 rounded bg-stone-50 border border-stone-100 p-2 leading-relaxed">
                                    <span className="text-amber-700 font-medium">Fix:</span> {fac.recommendation}
                                    {fac.action_path && (
                                        <Link to={fac.action_path}
                                              className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-stone-900 underline decoration-dotted hover:decoration-solid"
                                              data-testid={`hs-fix-${fac.id}`}>
                                            {fac.action_label || "Open"} <ArrowRight size={11} />
                                        </Link>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="text-[11px] text-stone-400 mt-6 text-right">
                Computed at {new Date(data.computed_at).toLocaleString()}
            </div>
        </div>
    );
}
